import clone from "clone";
import React from "react";
import { SurveySegment, segmentListIsInLists, SurveyReportDataSet, SurveyReportBuilder, Survey, SurveyReport, UnscoredSelectionStats, segmentListsMatch } from "../../../../../../../amorg-common";
import { PageSection } from "../../../../../../../designSystem/PageSection";
import { amorgLocalStorage } from "../../../../../../../utils/amorgLocalStorage";
import { PrintButtonWithContent } from "../../../../../../_components/PrintButtonWithContent";
import PrintSurveyReportPage from "../../../../../../_components/PrintSurveyReportPage";
import { WorkplaceAssessmentComponentProps } from "../../_components/WorkplaceAssessmentComponentProps";
import { SurveyReportPanel } from "../_components/SurveyReportPanel";
import { SurveyReportViewPanel } from "../_components/SurveyReportViewPanel";

type OverviewPanelState = {
    segmentOptions:SurveySegmentFilterOption[]
};

type OverviewReportPanelProps = WorkplaceAssessmentComponentProps & {
    setHeaderButtons: React.Dispatch<React.SetStateAction<React.ReactNode>>,
};

export function OverviewReportPanel(props:OverviewReportPanelProps):JSX.Element
{
    const [state, setState] = React.useState<OverviewPanelState>({
        segmentOptions:[],
    });

    React.useEffect(() => {
        const getData = async () => {
            if(!props.survey || !props.surveyReport)
                return;

            const segmentOptions = readSegmentFilterFromStorage(props.survey, props.surveyReport);
            if(segmentOptions.every(o => !o.selected))
            {
                segmentOptions.forEach(o => o.selected = true);
            }
            writeSegmentFilterToStorage(props.survey.id, segmentOptions.map(o => o.segments));

            const newState:OverviewPanelState = {
                segmentOptions
            };
            setState(newState);
        };
        getData();
    }, []);

    function handleFilterChanged(filter: SurveySegment[][]) {
        if(!props.survey)
            return;

        const cloned = clone(state.segmentOptions);
        for(const option of cloned)
        {
            option.selected = segmentListIsInLists(filter, option.segments);
        }
        setState({
            segmentOptions: cloned
        });
        writeSegmentFilterToStorage(props.survey.id, filter);
    }

    const selectedSegments = React.useMemo(
        () => state.segmentOptions.filter(o => o.selected).map(o => o.segments),
        [state.segmentOptions]
    );

    const hasSegments = !!props.survey && props.survey.segmentations.length > 0;

    const dataset:SurveyReportDataSet | null = React.useMemo(() => {
            
        return props.survey && props.surveyReport
            ? new SurveyReportBuilder(props.survey, props.surveyReport).getData(selectedSegments)
            : null;
        },
        [props.survey, props.surveyReport, selectedSegments]
    );

    React.useEffect(() => {
        if(dataset && props.survey !== null)
        {
            const s = props.survey;
            props.setHeaderButtons(
                <PrintButtonWithContent documentTitle={props.survey.title}>
                    {() =>
                        <PrintSurveyReportPage
                            survey={s}
                            data={dataset}
                            segmentFilter={selectedSegments}
                        />
                    }
                </PrintButtonWithContent>
            );
        }
    }, [dataset, props.setHeaderButtons, props.survey, selectedSegments]);

    React.useEffect(() => {
        return () => props.setHeaderButtons(undefined);
    }, [props.setHeaderButtons]);

    return (
        <>
            {/* Handle report sharing for segmentless surveys */}
            {props.activeUser && props.survey && hasSegments &&
                <PageSection
                    header={{
                        title: 'Valgte segmenter',
                        avatar:{ text: selectedSegments.length.toString() },
                        defaultCollapsed: false  
                    }}>

                    <SurveyReportViewPanel 
                        siteId={props.activeUser.siteId} 
                        survey={props.survey}
                        segmentReports={state.segmentOptions.map(o => ({ segments: o.segments, answerCount: o.answerCount }))}
                        segmentFilter={selectedSegments}
                        onFilterChanged={handleFilterChanged} />

                </PageSection>
            }

            {dataset && props.survey &&
                <SurveyReportPanel
                    siteId={props.activeUser.siteId}
                    survey={props.survey}
                    data={dataset}
                    segments={selectedSegments}
                    showComments={true} />
            }
        </>
    );
}

type SurveySegmentFilterOption = {
    segments: SurveySegment[],
    answerCount:number,
    selected:boolean
}

function readSegmentFilterFromStorage(survey:Survey, report:SurveyReport): SurveySegmentFilterOption[]
{
    const options:SurveySegmentFilterOption[] = report.selectionReports.map(sr => ({ 
        segments: sr.segments, 
        answerCount:sr.questionIndexedSummaries.length === 0
            ? 0
            : new UnscoredSelectionStats(sr.questionIndexedSummaries[0].selectionSummary).totalSelections,
        selected: false 
    }));
    if(survey.segmentations.length === 0)
        return [];
    
    const storedFilterKey = amorgLocalStorage.surveySegmentation.filter.getKey('survey-report', survey.id);
    const storedFilter = amorgLocalStorage.surveySegmentation.filter.read(storedFilterKey);

    // Get rid of any stored filter options that don't match the current report data, then apply the stored filter to the available options
    for(const itm of storedFilter)
    {
        for(const option of options)
        {
            if(segmentListsMatch(option.segments, itm))
            {
                option.selected = true;
                break;
            }
        }
    }

    return options;
}

function writeSegmentFilterToStorage(surveyId:string, filter: SurveySegment[][])
{
    const filterKey = amorgLocalStorage.surveySegmentation.filter.getKey('survey-report', surveyId);
    amorgLocalStorage.surveySegmentation.filter.write(filterKey, filter);
}